import { useStore } from '@/store/useStore'

/** Outcome of reading a token out of storage, keeping "absent" apart from "unreadable". */
export type StoredTokenRead =
  | { status: 'present'; token: string }
  | { status: 'absent' }
  /** Storage itself threw — a private-mode quirk or a transition, not a signed-out user. */
  | { status: 'unreadable' }

/**
 * Read one token from storage, reporting how the read went.
 *
 * The distinction is the point. `localStorage` can throw rather than return
 * null — blocked site data, a private window, a page mid-transition — and
 * collapsing that into "no token" is what let a momentary read failure be read
 * as "signed out", after which the code deleted the token and made it true
 * (BRAI-422).
 */
function readStoredToken(key: string): StoredTokenRead {
  let raw: string | null
  try {
    raw = localStorage.getItem(key)
  } catch {
    return { status: 'unreadable' }
  }
  return raw ? { status: 'present', token: raw } : { status: 'absent' }
}

export function readAccessToken(): StoredTokenRead {
  return readStoredToken('accessToken')
}

export function readRefreshToken(): StoredTokenRead {
  return readStoredToken('refreshToken')
}

export function getAccessToken(): string | null {
  const read = readAccessToken()
  return read.status === 'present' ? read.token : null
}

export function getRefreshToken(): string | null {
  const read = readRefreshToken()
  return read.status === 'present' ? read.token : null
}

/**
 * Whether the app should treat this browser as signed in.
 *
 * An unreadable store answers `true`. It is the safer wrong answer of the two:
 * a wrongly-admitted user meets a 401 from the server and is signed out
 * properly, while a wrongly-rejected one is thrown out of a meeting that was
 * working — which is the bug this exists to prevent.
 */
export function hasAccessToken(): boolean {
  return readAccessToken().status !== 'absent'
}

export function storeSessionTokens(accessToken: string, refreshToken?: string | null): void {
  try {
    localStorage.setItem('accessToken', accessToken)
    if (refreshToken) localStorage.setItem('refreshToken', refreshToken)
  } catch {
    /* A store that will not take the tokens leaves this session in memory only. */
  }
}

/**
 * Best-effort decode of the current user id from the JWT `accessToken`'s
 * `sub` claim. This never verifies the token's signature — it's a
 * non-authoritative "who does this belong to" hint (e.g. for tagging
 * outbound WebSocket messages to an external proxy), not a security check —
 * so any decode/parse failure degrades silently to `null` instead of throwing.
 *
 * @returns The `sub` claim from the decoded JWT payload, or `null` if there's
 *   no access token, the token is malformed, or the payload has no string `sub`.
 */
export function getCurrentUserId(): string | null {
  const token = getAccessToken()
  if (!token) return null

  const payloadSegment = token.split('.')[1]
  if (!payloadSegment) return null

  try {
    // JWT payloads are base64url-encoded, not standard base64: convert the
    // URL-safe characters back and re-pad to a multiple of 4 before `atob`,
    // since `atob` can reject unpadded/non-standard base64 in some
    // environments (including this project's jsdom test environment).
    const base64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=')
    const decoded = atob(padded)
    const payload = JSON.parse(decoded) as { sub?: unknown }
    return typeof payload.sub === 'string' ? payload.sub : null
  } catch {
    return null
  }
}

/**
 * Display name of the signed-in user, for tagging outbound config frames.
 *
 * Read from the session store rather than the JWT: the token carries `sub`,
 * not the name. A blank name is treated as absent so the caller can leave the
 * key off the wire instead of sending an empty string.
 *
 * @returns The trimmed display name, or null when nobody is signed in or the name is blank.
 */
export function getCurrentUserName(): string | null {
  const name = useStore.getState().user?.displayName.trim()
  return name ? name : null
}

/** Why a session ended, recorded so a report of being signed out names a cause. */
export type SessionEndReason =
  | 'server_rejected_token'
  | 'refresh_failed'
  | 'user_signed_out'

/**
 * End the session and send the browser to the login screen.
 *
 * Takes a reason and logs it. Ending a session is destructive and, until this
 * carried one, a user reporting that they were thrown out gave us nothing to
 * tell the causes apart — the only thing on screen was the login form, which
 * looks identical whichever path got there (BRAI-422).
 */
export function clearSessionAndRedirectToLogin(reason: SessionEndReason): void {
  // eslint-disable-next-line no-console
  console.warn(`[session] signed out: ${reason}`)
  useStore.getState().logout()

  if (window.location.pathname !== '/login') {
    window.location.replace('/login')
  }
}
